import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { TrashIcon, FileCodeIcon, SparkleIcon, LightningIcon } from "@phosphor-icons/react";

export const HistoryDialog = ({ open, onOpenChange, onLoad }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/history");
      setItems(data);
    } catch { toast.error("Failed to load history"); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(); }, [open]);

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/history/${id}`);
    setItems((s) => s.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="history-dialog" className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-200">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <FileCodeIcon className="h-5 w-5 text-cyan-400" /> Generation history
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          {loading && <p className="text-sm text-zinc-500 py-8 text-center">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-zinc-600 py-8 text-center">No saved generations yet.</p>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`history-item-${it.id}`}
              onClick={() => { onLoad(it); onOpenChange(false); }}
              className="group cursor-pointer rounded-sm border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700 px-3 py-2.5 transition-colors duration-150"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-medium ${
                      it.mode === "ai" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" : "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
                    }`}>
                      {it.mode === "ai" ? <SparkleIcon className="h-3 w-3" /> : <LightningIcon className="h-3 w-3" />}
                      {it.mode === "ai" ? "AI" : "Template"}
                    </span>
                    {it.project_name && <span className="text-xs font-medium text-zinc-100 truncate max-w-[160px]">{it.project_name}</span>}
                    {it.project_code && <span className="rounded-sm border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300">{it.project_code}</span>}
                    <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">v{it.version || "0.1"}</span>
                    <span className="font-mono text-[11px] uppercase text-zinc-500">{it.vendor}</span>
                    <span className="text-[11px] text-zinc-600">· {it.devices_count} devices</span>
                  </div>
                  {it.customer && <p className="text-[11px] text-zinc-500 mb-0.5">Client: {it.customer}</p>}
                  <p className="text-xs text-zinc-400 truncate">{it.requirement}</p>
                </div>
                <button data-testid={`history-delete-${it.id}`} onClick={(e) => remove(it.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-rose-400 transition-all duration-150 shrink-0">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
